import assertNever from "assert-never";
import React, { useMemo } from "react";
import { OrientationLanguages, PassportDocumentVerificationStatus } from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { translate } from "../../../../translate";
import { CHECK_CIRCLE, DOT_CIRCLE, MINUS_CIRCLE, TIMES_CIRCLE } from "../../../../utils/svgs";
import { AppIcon } from "../../../Generic/AppIcon";

const MINIMUM_EXPIRY_DAYS = 30;

interface Props {
  expiryDays: number | null;
  verificationStatus: PassportDocumentVerificationStatus | null;
  language: OrientationLanguages;
}

const PassportCategoryStatus = (props: Props) => {
  const isAboutToExpire = useMemo(() => {
    return props.expiryDays !== null && props.expiryDays <= MINIMUM_EXPIRY_DAYS;
  }, [props.expiryDays]);

  const documentStatusColor = useMemo(() => {
    switch (props.verificationStatus) {
      case "DECLINED":
        return "RED";
      case "APPROVED":
        return props.expiryDays === null || props.expiryDays > 0 ? "GREEN" : "RED";
      case "PENDING":
        return "YELLOW";
      default:
        return "GRAY";
    }
  }, [props.verificationStatus, props.expiryDays]);

  const statusIcon = useMemo(() => {
    switch (documentStatusColor) {
      case "RED":
        return TIMES_CIRCLE;
      case "GREEN":
        return CHECK_CIRCLE;
      case "YELLOW":
        return MINUS_CIRCLE;
      case "GRAY":
        return DOT_CIRCLE;
      default:
        assertNever(documentStatusColor);
    }
  }, [documentStatusColor]);

  const documentDescription = useMemo(() => {
    switch (props.verificationStatus) {
      case "DECLINED":
        return translate(STRINGS.DOCUMENT_REJECTED);
      case "PENDING":
        return translate(STRINGS.WAITING_FOR_APPROVAL);
      case "APPROVED":
        if (props.expiryDays === null || !isAboutToExpire) {
          return translate(STRINGS.VERIFIED);
        }

        if (props.expiryDays === 1) {
          return translate(STRINGS.EXPIRES_IN_ONE_DAY);
        }

        if (props.expiryDays > 1) {
          return translate(STRINGS.EXPIRES_IN_X_DAYS).replace("%d", props.expiryDays.toString());
        }

        return translate(STRINGS.DOCUMENT_HAS_EXPIRED);
      case null:
        return translate(STRINGS.UPLOAD_DOCUMENT);
      default:
        assertNever(props.verificationStatus);
    }
  }, [props.expiryDays, props.verificationStatus, isAboutToExpire]);

  return (
    <div className="PassportCategoryStatus">
      <AppIcon icon={statusIcon} size={16} />
      <span className="__documentDescription">{documentDescription}</span>
    </div>
  );
};

export default React.memo(PassportCategoryStatus);
